import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Zap } from 'lucide-react';
import { MCPanelHeader } from './MCSection';
/** Ceiling on the Telegraph entrance animation (translate 520ms + flash 1400ms + safety). */
const TELEGRAPH_DURATION_MS = 1500;
const typeColor = {
    BookingCreate: 'var(--mc-cyan)',
    SessionStart: 'var(--mc-cyan)',
    SessionEnd: 'var(--mc-purple)',
    PaymentSettle: 'var(--mc-magenta)',
    StatusChange: 'var(--mc-gray)',
    QuickSale: 'var(--mc-magenta)',
    ReceiptUpload: 'var(--mc-amber)',
    ReceiptReview: 'var(--mc-magenta)',
    OcrAnalyzer: 'var(--mc-amber)',
    OcrHealth: 'var(--mc-green)',
};
function formatTime(d) {
    return d.toLocaleTimeString('en-CA', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZone: 'America/Halifax',
    });
}
function formatDate(d) {
    return d.toLocaleDateString('en-CA', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        timeZone: 'America/Halifax',
    });
}
export function MCDataStream({ events, maxEntries = 30, onSimulate, onOpenEvent, }) {
    const listRef = useRef(null);
    const capped = events.slice(0, maxEntries);
    // Track previously-seen event IDs so only genuinely new entries animate.
    // First render is marked "seen" without animating (avoids animating the backlog).
    const seenIdsRef = useRef(null);
    const newIds = useMemo(() => {
        if (seenIdsRef.current === null) {
            return new Set();
        }
        const previous = seenIdsRef.current;
        const fresh = new Set();
        for (const ev of capped) {
            if (!previous.has(ev.id))
                fresh.add(ev.id);
        }
        return fresh;
    }, [capped]);
    useEffect(() => {
        seenIdsRef.current = new Set(capped.map((ev) => ev.id));
    }, [capped]);
    // Animation class lifetime decoupled from detection so the class survives
    // intermediate re-renders (e.g. the dashboard's 1-second clock tick) for
    // the full animation duration.
    const [animatingIds, setAnimatingIds] = useState(() => new Set());
    const timersRef = useRef(new Map());
    // Tracks ids that have EVER played the telegraph animation. These rows
    // must not fall back to the subtle `.mc-stream-entry` fade (mcStreamIn)
    // once the telegraph class is removed — otherwise cascade replaces the
    // animation declaration and mcStreamIn replays from frame 0, producing
    // a post-fade flicker.
    const everAnimatedRef = useRef(new Set());
    useEffect(() => {
        if (newIds.size === 0)
            return;
        setAnimatingIds((prev) => {
            let changed = false;
            const next = new Set(prev);
            newIds.forEach((id) => {
                if (!next.has(id)) {
                    next.add(id);
                    everAnimatedRef.current.add(id);
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
        // Bound the ever-animated set so it can't grow without limit.
        const cap = Math.max(maxEntries * 2, 50);
        if (everAnimatedRef.current.size > cap) {
            const excess = everAnimatedRef.current.size - cap;
            const iter = everAnimatedRef.current.values();
            for (let i = 0; i < excess; i += 1) {
                const oldest = iter.next().value;
                if (oldest !== undefined)
                    everAnimatedRef.current.delete(oldest);
            }
        }
        newIds.forEach((id) => {
            if (timersRef.current.has(id))
                return;
            const handle = window.setTimeout(() => {
                timersRef.current.delete(id);
                setAnimatingIds((prev) => {
                    if (!prev.has(id))
                        return prev;
                    const next = new Set(prev);
                    next.delete(id);
                    return next;
                });
            }, TELEGRAPH_DURATION_MS);
            timersRef.current.set(id, handle);
        });
    }, [newIds, maxEntries]);
    useEffect(() => {
        return () => {
            timersRef.current.forEach((handle) => window.clearTimeout(handle));
            timersRef.current.clear();
        };
    }, []);
    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTop = 0;
        }
    }, [events.length]);
    return (_jsxs("div", { className: "flex flex-col h-full min-h-0", children: [_jsx(MCPanelHeader, { label: "Data Stream", meta: "Real-Time", right: _jsxs(_Fragment, { children: [onSimulate && (_jsxs("button", { type: "button", onClick: onSimulate, className: "mc-chip mc-mono", "aria-label": "Simulate event", children: [_jsx(Zap, { className: "h-3.5 w-3.5" }), "Simulate"] })), _jsxs("div", { className: "mc-meta mc-mono text-right", children: [_jsx("div", { children: formatTime(new Date()) }), _jsx("div", { className: "mc-meta-dim mt-1", children: formatDate(new Date()) })] })] }) }), _jsx("div", { ref: listRef, className: "flex-1 min-h-0 overflow-y-auto pr-1 mc-scroll-thin", children: capped.length === 0 ? (_jsx("div", { className: "mc-meta mc-mono py-6 text-center", children: "// awaiting events" })) : (_jsx("ul", { className: "flex flex-col gap-4", children: capped.map((ev) => {
                        const isAnimating = animatingIds.has(ev.id);
                        const hasEverAnimated = everAnimatedRef.current.has(ev.id);
                        const isActionable = Boolean(ev.linkHref && onOpenEvent);
                        const className = [
                            'mc-mono text-[13px] leading-snug',
                            // Subtle mount fade only for rows that never got the telegraph
                            // treatment. Suppressing it on ever-animated rows prevents the
                            // animation declaration from replaying when the telegraph class
                            // is removed.
                            !isAnimating && !hasEverAnimated ? 'mc-stream-entry' : '',
                            isAnimating ? 'mc-stream-new' : '',
                            isAnimating ? 'mc-stream-new--telegraph' : '',
                        ]
                            .filter(Boolean)
                            .join(' ');
                        const content = (_jsxs(_Fragment, { children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: "font-semibold", style: { color: typeColor[ev.type] }, children: ev.type }), ev.secondary && (_jsx("span", { className: "text-[color:var(--mc-white)] font-medium", children: ev.secondary }))] }), _jsxs("div", { className: "text-[color:var(--mc-gray)] mt-1 break-all", children: [formatTime(ev.timestamp), " \u00B7 ", ev.primary] }), ev.meta && (_jsxs("div", { className: "text-[color:var(--mc-gray-dim)] mt-0.5", children: ["\"", ev.meta, "\""] }))] }));
                        return (_jsx("li", { className: className, children: isActionable ? (_jsx("button", { type: "button", onClick: () => onOpenEvent?.(ev), className: "w-full text-left rounded-sm px-1 -mx-1 py-0.5 transition-colors hover:bg-[rgba(29,224,197,0.06)] focus:outline-none focus-visible:bg-[rgba(29,224,197,0.08)]", "aria-label": `Open ${ev.type} event: ${ev.primary}`, children: content })) : (_jsx("div", { children: content })) }, ev.id));
                    }) })) })] }));
}
